import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { parseAuthCallbackError } from '@/lib/authCallback';
import { resetPasswordErrorI18nKey } from '@/lib/authFormErrors';
import { AuthShell } from './AuthShell';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

type OtpState = 'idle' | 'pending' | 'ok' | 'fail';

function initialOtpState(): OtpState {
  const params = new URLSearchParams(window.location.search);
  const tokenHash = params.get('token_hash');
  const type = params.get('type');
  return tokenHash && type === 'recovery' ? 'pending' : 'idle';
}

/**
 * Recovery landing page. Two entry styles:
 * - Resend link with `token_hash` + `type=recovery` (CLISUP-65). We call
 * verifyOtp; GoTrue Site URL is never involved.
 * - Legacy GoTrue redirect after resetPasswordForEmail (PKCE / hash tokens).
 * detectSessionInUrl may already have a recovery session.
 *
 * Staff and portal share this component. `/portal/reset-password` is an alias
 * so portal users return to `/portal/dashboard` after saving.
 */
export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, memberships } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [callbackError, setCallbackError] = useState(false);
  const [otpState, setOtpState] = useState<OtpState>(initialOtpState);

  const isPortalPath = location.pathname.startsWith('/portal');
  const requestNewLinkTo = isPortalPath ? '/portal/login' : '/forgot-password';

  useEffect(() => {
    if (parseAuthCallbackError(window.location.search, window.location.hash)) {
      setCallbackError(true);
    }
  }, []);

  useEffect(() => {
    if (otpState !== 'pending') return;

    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type');
    if (!tokenHash || type !== 'recovery') {
      setOtpState('fail');
      return;
    }

    let cancelled = false;
    void (async () => {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      });
      if (cancelled) return;
      window.history.replaceState({}, '', window.location.pathname);
      setOtpState(verifyError ? 'fail' : 'ok');
    })();

    return () => {
      cancelled = true;
    };
  }, [otpState]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      const {
        data: { user },
      } = await supabase.auth.getUser();
      let dest = '/dashboard';
      if (isPortalPath) {
        dest = '/portal/dashboard';
      } else if (memberships.length === 0 && user) {
        const { data: patients } = await supabase
          .from('patients')
          .select('id')
          .eq('portal_user_id', user.id)
          .eq('status', 'active')
          .limit(1);
        if (patients && patients.length > 0) {
          dest = '/portal/dashboard';
        }
      }

      navigate(dest, { replace: true });
    } catch (err) {
      setError(t(resetPasswordErrorI18nKey(err)));
      setSubmitting(false);
    }
  };

  const showSpinner = status === 'initializing' || otpState === 'pending';
  const linkInvalid =
    callbackError || otpState === 'fail' || (otpState === 'idle' && status === 'unauthenticated');

  if (showSpinner) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ds-bg">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-ds-text" />
      </div>
    );
  }

  if (linkInvalid) {
    return (
      <AuthShell
        title={t('auth.reset_password.link_expired_title')}
        subtitle={t('auth.reset_password.link_expired_body')}
        footer={
          <Link to={requestNewLinkTo} className="font-medium text-ds-teal hover:text-ds-teal-700">
            {t('auth.reset_password.request_new_link')}
          </Link>
        }
      >
        <ErrorAlert message={t('auth.reset_password.link_expired_body')} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.reset_password.title')} subtitle={t('auth.reset_password.subtitle')}>
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <PasswordInput
          label={t('auth.reset_password.password')}
          name="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordInput
          label={t('auth.reset_password.password_confirm')}
          name="password_confirm"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.reset_password.submitting') : t('auth.reset_password.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
